import * as React from 'react';
import { Trans } from 'react-i18next';
import { decide } from '../../features/shared/utils/decide.ts';
import type { TranslationFunction } from '../../lib/hooks/use-locale.ts';
import { useLocale } from '../../lib/hooks/use-locale.ts';
import type { ToastActionElement, ToastProps } from './toast.tsx';

const TOAST_LIMIT = 1;
const TOAST_REMOVE_DELAY = 1000000;

type ToasterToast = ToastProps & {
    id: string;
    title?: React.ReactNode;
    description?: React.ReactNode;
    action?: ToastActionElement;
    icon?: 'success' | 'copy' | 'info' | 'warning';
};

type BaseToasterToastProps = Omit<ToasterToast, 'id' | 'description'>;

let count = 0;

function genId(): string {
    count = (count + 1) % Number.MAX_SAFE_INTEGER;
    return count.toString();
}

type ActionType = {
    ADD_TOAST: 'ADD_TOAST';
    UPDATE_TOAST: 'UPDATE_TOAST';
    DISMISS_TOAST: 'DISMISS_TOAST';
    REMOVE_TOAST: 'REMOVE_TOAST';
};

type Action =
    | {
          type: ActionType['ADD_TOAST'];
          toast: ToasterToast;
      }
    | {
          type: ActionType['UPDATE_TOAST'];
          toast: Partial<ToasterToast>;
      }
    | {
          type: ActionType['DISMISS_TOAST'];
          toastId?: ToasterToast['id'];
      }
    | {
          type: ActionType['REMOVE_TOAST'];
          toastId?: ToasterToast['id'];
      };

type State = {
    toasts: ToasterToast[];
};

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

const addToRemoveQueue = (toastId: string): void => {
    if (toastTimeouts.has(toastId)) {
        return;
    }

    const timeout = setTimeout(() => {
        toastTimeouts.delete(toastId);
        dispatch({
            type: 'REMOVE_TOAST',
            toastId: toastId,
        });
    }, TOAST_REMOVE_DELAY);

    toastTimeouts.set(toastId, timeout);
};

export const reducer = (state: State, action: Action): State => {
    switch (action.type) {
        case 'ADD_TOAST':
            return {
                ...state,
                toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT),
            };

        case 'UPDATE_TOAST':
            return {
                ...state,
                toasts: state.toasts.map((t) => (t.id === action.toast.id ? { ...t, ...action.toast } : t)),
            };

        case 'DISMISS_TOAST': {
            const { toastId } = action;

            // ! Side effects ! - This could be extracted into a dismissToast() action,
            // but I'll keep it here for simplicity
            if (toastId) {
                addToRemoveQueue(toastId);
            } else {
                for (const toast of state.toasts) {
                    addToRemoveQueue(toast.id);
                }
            }

            return {
                ...state,
                toasts: state.toasts.map((t) =>
                    t.id === toastId || toastId === undefined
                        ? {
                              ...t,
                              open: false,
                          }
                        : t,
                ),
            };
        }
        case 'REMOVE_TOAST':
            if (action.toastId === undefined) {
                return {
                    ...state,
                    toasts: [],
                };
            }
            return {
                ...state,
                toasts: state.toasts.filter((t) => t.id !== action.toastId),
            };
    }
};

const listeners: ((state: State) => void)[] = [];

let memoryState: State = { toasts: [] };

function dispatch(action: Action): void {
    memoryState = reducer(memoryState, action);
    for (const listener of listeners) {
        listener(memoryState);
    }
}

// PREP-170: a toast can carry a custom description OR exactly one link, but never both.
// Passing both silently dropped the link, leaving callers with an unclickable toast.
type ToasterProps = BaseToasterToastProps &
    (
        | { description?: React.ReactNode; createdLink?: never; editedLink?: never; deletedLink?: never }
        | { createdLink: React.ReactElement; description?: never; editedLink?: never; deletedLink?: never }
        | { editedLink: React.ReactElement; description?: never; createdLink?: never; deletedLink?: never }
        | { deletedLink: React.ReactElement; description?: never; createdLink?: never; editedLink?: never }
    );

export function toast({ ...props }: ToasterProps) {
    const id = genId();

    const update = (props: ToasterToast): void => {
        dispatch({
            type: 'UPDATE_TOAST',
            toast: { ...props, id },
        });
    };

    const dismiss = (): void => {
        dispatch({ type: 'DISMISS_TOAST', toastId: id });
    };

    dispatch({
        type: 'ADD_TOAST',
        toast: {
            ...props,
            id,
            open: true,
            onOpenChange: (open) => {
                if (!open) {
                    dismiss();
                }
            },
        },
    });

    return {
        id: id,
        dismiss,
        update,
    };
}

export function useToast() {
    const [state, setState] = React.useState<State>(memoryState);
    const { t } = useLocale();

    React.useEffect(() => {
        listeners.push(setState);
        return () => {
            const index = listeners.indexOf(setState);
            if (index !== -1) {
                listeners.splice(index, 1);
            }
        };
    }, [state]);

    return {
        ...state,
        toast: buildToastMessage({ t }),
        dismiss: (toastId?: string) => {
            dispatch({ type: 'DISMISS_TOAST', toastId });
        },
    };
}

const linkSelector = ({
    key,
    linkTag,
    t,
}: {
    key: string;
    linkTag?: React.ReactElement;
    t: TranslationFunction;
}) => {
    // eslint-disable-next-line react/display-name
    return () => {
        return <Trans t={t} ns="common" i18nKey={key} components={linkTag ? { linkTag } : undefined} />;
    };
};

const buildToastMessage =
    ({ t }: { t: TranslationFunction }) =>
    ({ createdLink, editedLink, deletedLink, ...props }: ToasterProps) => {
        const description = decide([
            {
                when: [!!createdLink],
                then: linkSelector({ key: 'toast__created_success_body', linkTag: createdLink, t }),
            },
            {
                when: [!!editedLink],
                then: linkSelector({ key: 'toast__edited_success_body', linkTag: editedLink, t }),
            },
            {
                when: [!!deletedLink],
                then: linkSelector({ key: 'toast__deleted_success_body', linkTag: deletedLink, t }),
            },
        ]);

        return toast({
            description,
            ...props,
        });
    };
